import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import clsx from 'clsx'
import React, { useContext, useEffect, useState } from 'react'
import { Chevron } from './Chevron'
import { NavContext } from './NavContext'
import styles from './NavItem.module.scss'
import { SubItems } from './SubItems'

export type TNavItemVariant = 'filled' | 'transparent'

export interface INavItemProps extends React.HTMLAttributes<HTMLButtonElement> {
  /**
   * The content of the navigation item.
   */
  children?: React.ReactNode
  /**
   * The content of the sub-items that will be displayed when the navigation item is clicked.
   */
  subItems?: React.ReactNode
  /**
   * Whether the navigation item is active. If `isActive=true` and has `subItems` the navigation item will be expanded.
   */
  isActive?: boolean
  /**
   * An icon to be displayed next to the content of the navigation item.
   */
  icon?: React.ReactNode
}

export const NavItem = ({
  children,
  subItems,
  isActive,
  className,
  icon,
  onClick,
  ...props
}: INavItemProps) => {
  const hasSubItems = Boolean(subItems)
  const { variant, isCollapsed } = useContext(NavContext)
  const [isOpen, setIsOpen] = useState(isActive)
  const { isFloatingOpen, floatingStyles, refs, context, getReferenceProps, getFloatingProps } =
    useFloatingSubMenu({ enabled: hasSubItems && isCollapsed })

  useEffect(() => setIsOpen(isActive), [isActive])

  const handleClick = () => {
    if (!subItems) return
    setIsOpen((prev) => !prev)
  }

  return (
    <div>
      <button
        className={clsx(
          styles.wrapper,
          styles[variant],
          {
            [styles.active]: isOpen || isActive,
            [styles.collapsed]: isCollapsed,
          },
          className,
        )}
        ref={refs.setReference}
        {...getReferenceProps({
          ...props,
          onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            onClick?.(e)
            handleClick()
          },
        })}
      >
        <div className={styles.body}>
          {icon ? <div className={styles.icon}>{icon}</div> : null}
          <div className={styles.children}>{children}</div>
        </div>
        <Chevron isOpen={isOpen} hasSubItems={hasSubItems} isCollapsed={isCollapsed} />
      </button>
      <SubItems isOpen={isOpen} isCollapsed={isCollapsed}>
        {subItems}
      </SubItems>
      {isFloatingOpen && hasSubItems && isCollapsed && (
        <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
          <div
            ref={refs.setFloating}
            className={clsx(styles.floating, styles[variant])}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {subItems}
          </div>
        </FloatingFocusManager>
      )}
    </div>
  )
}

const useFloatingSubMenu = ({ enabled }: { enabled?: boolean }) => {
  const [isFloatingOpen, setIsFloatingOpen] = useState(false)

  const { floatingStyles, refs, context } = useFloating<HTMLDivElement>({
    open: isFloatingOpen,
    onOpenChange: setIsFloatingOpen,
    placement: 'right-start',
    middleware: [offset({ mainAxis: 16 }), flip(), shift()],
    whileElementsMounted: autoUpdate,
  })
  const hover = useHover(context, {
    enabled,
    delay: {
      open: 0,
      close: 50,
    },
    handleClose: safePolygon({ buffer: 1 }),
  })

  const role = useRole(context, { enabled, role: 'menu' })
  const dismiss = useDismiss(context, { enabled })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, role, dismiss])

  return {
    isFloatingOpen,
    setIsFloatingOpen,
    floatingStyles,
    refs,
    context,
    getReferenceProps,
    getFloatingProps,
  }
}
